// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO (notifications rework 2026-10, design/notifications-rework b-desk): Settings >
// Notifications > Notify me about on desktop. One row per source, one column per channel (phone
// push, this computer, pop-up inside Konvo, sound). A cell a source never uses shows a dash with
// the reason. Agents carries its events and which chats under its row.

import Agents from '@app/features/konvo_agents/state/Agents';
import AgentsAccess from '@app/features/konvo_agents/state/AgentsAccess';
import {useNotificationSourceItems} from '@app/features/konvo_notifications/components/KonvoNotificationSources';
import styles from '@app/features/konvo_notifications/components/NotificationMatrix.module.css';
import KonvoNotificationPrefs, {
	CHANNELS_FOR,
	NOTIFICATION_CHANNELS,
	type NotificationChannel,
	type NotificationChannelSource,
} from '@app/features/konvo_notifications/state/KonvoNotificationPrefs';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {CheckIcon} from '@app/features/ui/icons/Icons';
import {Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {AgentsPrefs} from '@fluxer/schema/src/domains/konvo/Agents';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconTerminal2} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect} from 'react';

const CHANNEL_DESCRIPTORS: Record<NotificationChannel, MessageDescriptor> = {
	push: msg({
		message: 'Phone push',
		comment: 'Settings > Notifications > Notify me about: column heading, a push notification on your phone.',
	}),
	desktop: msg({
		message: 'This computer',
		comment:
			'Settings > Notifications > Notify me about: column heading, a notification from the desktop app or browser on this computer.',
	}),
	popup: msg({
		message: 'Pop-up',
		comment: 'Settings > Notifications > Notify me about: column heading, the banner inside Konvo while it is open.',
	}),
	sound: msg({
		message: 'Sound',
		comment: 'Settings > Notifications > Notify me about: column heading, the notification sound.',
	}),
};
const SOURCE_DESCRIPTOR = msg({
	message: 'Source',
	comment: 'Settings > Notifications > Notify me about: heading of the first column (Konvo chats, Teams, WhatsApp...).',
});
const CELL_LABEL_DESCRIPTOR = msg({
	message: '{source}: {channel}',
	comment:
		'Settings > Notifications > Notify me about: accessible name of one checkbox, e.g. "WhatsApp: Phone push". Keep {source} and {channel}.',
});
const NO_POPUP_FOR_MAIL_DESCRIPTOR = msg({
	message: 'Mail has no pop-ups inside Konvo.',
	comment: 'Settings > Notifications > Notify me about: tooltip on the empty Mail / Pop-up cell.',
});
const CLICKUP_PUSH_ONLY_DESCRIPTOR = msg({
	message: 'ClickUp activity comes as a phone push only.',
	comment: 'Settings > Notifications > Notify me about: tooltip on the empty ClickUp cells.',
});
const AGENTS_PUSH_ONLY_DESCRIPTOR = msg({
	message: 'Agents tell you with a push that opens the chat.',
	comment:
		'Settings > Notifications > Notify me about: tooltip on the empty Agents cells. Agents notices (finished, failed, needs you) only come as a push notification, never as a message in the Assistant DM.',
});
const NOT_USED_DESCRIPTOR = msg({
	message: 'Not used',
	comment: 'Settings > Notifications > Notify me about: accessible text of an empty cell a source never uses.',
});

function notUsedReason(source: NotificationChannelSource) {
	switch (source) {
		case 'mail':
			return NO_POPUP_FOR_MAIL_DESCRIPTOR;
		case 'clickup':
			return CLICKUP_PUSH_ONLY_DESCRIPTOR;
		default:
			return AGENTS_PUSH_ONLY_DESCRIPTOR;
	}
}

const Cell: React.FC<{source: NotificationChannelSource; label: string; channel: NotificationChannel}> = observer(
	({source, label, channel}) => {
		const {i18n} = useLingui();
		if (!CHANNELS_FOR[source].includes(channel)) {
			return (
				<span className={styles.cell}>
					<Tooltip text={i18n._(notUsedReason(source))}>
						<span className={styles.notUsed} role="img" aria-label={i18n._(NOT_USED_DESCRIPTOR)}>
							–
						</span>
					</Tooltip>
				</span>
			);
		}
		const on = KonvoNotificationPrefs.allowsChannel(source, channel);
		return (
			<span className={styles.cell}>
				<button
					type="button"
					role="checkbox"
					aria-checked={on}
					aria-label={i18n._(CELL_LABEL_DESCRIPTOR, {source: label, channel: i18n._(CHANNEL_DESCRIPTORS[channel])})}
					className={clsx(styles.box, on && styles.boxOn)}
					onClick={() => void KonvoNotificationPrefs.setChannel(source, channel, !on)}
					data-flx={`konvo-notifications.matrix.${source}.${channel}`}
				>
					{on ? <CheckIcon size={14} weight="bold" /> : null}
				</button>
			</span>
		);
	},
);

/** Events and which chats for Agents, under its row. */
const AgentsDetail: React.FC = observer(() => {
	const {t} = useLingui();
	const prefs = Agents.prefs;
	if (!prefs) return null;
	const save = (patch: Partial<AgentsPrefs>) =>
		void Agents.savePrefs(patch).catch(() => ToastCommands.error(t`Could not save. Try again.`));
	const events: ReadonlyArray<{key: string; label: string; on: boolean; patch: (on: boolean) => Partial<AgentsPrefs>}> =
		[
			{key: 'finished', label: t`Main agent finishes`, on: prefs.finished, patch: (finished) => ({finished})},
			{
				key: 'subagent-finished',
				label: t`Sub-agent finishes`,
				on: prefs.subagentFinished === true,
				patch: (subagentFinished) => ({subagentFinished}),
			},
			{key: 'needs-you', label: t`Needs me`, on: prefs.needsYou, patch: (needsYou) => ({needsYou})},
			{key: 'failed', label: t`Fails`, on: prefs.failed, patch: (failed) => ({failed})},
			{
				key: 'usage-alert',
				label: t`Plan at 90%`,
				on: prefs.usageAlert !== false,
				patch: (usageAlert) => ({usageAlert}),
			},
			{
				key: 'usage-reset',
				label: t`Limit resets`,
				on: prefs.usageReset !== false,
				patch: (usageReset) => ({usageReset}),
			},
		];
	return (
		<div className={styles.agents} data-flx="konvo-agents.notifications">
			<div className={styles.agentsGroup}>
				<span className={styles.agentsLabel}>{t`Tell me when a chat`}</span>
				<div className={styles.chips}>
					{events.map((event) => (
						<button
							key={event.key}
							type="button"
							aria-pressed={event.on}
							className={clsx(styles.chip, event.on && styles.chipOn)}
							onClick={() => save(event.patch(!event.on))}
							data-flx={`konvo-agents.notifications.${event.key}`}
						>
							{event.on ? <CheckIcon size={13} weight="bold" /> : null}
							{event.label}
						</button>
					))}
				</div>
			</div>
			<div className={styles.agentsGroup}>
				<span className={styles.agentsLabel}>
					{t({message: 'Pull requests', comment: 'Agents list switch: GitHub pull requests in every repository.'})}
				</span>
				<div className={styles.chips}>
					<button
						type="button"
						aria-pressed={prefs.pullUpdates !== false}
						className={clsx(styles.chip, prefs.pullUpdates !== false && styles.chipOn)}
						onClick={() => save({pullUpdates: prefs.pullUpdates === false})}
						data-flx="konvo-agents.notifications.pull-updates"
					>
						{prefs.pullUpdates !== false ? <CheckIcon size={13} weight="bold" /> : null}
						{t({
							message: 'Reviews, checks and merges',
							comment:
								'Notification settings, Agents: pushes about pull requests (asked to review, checks failed or ready on yours, reviews, merges, comments).',
						})}
					</button>
				</div>
			</div>
			<div className={styles.agentsGroup}>
				<span className={styles.agentsLabel}>{t`Which chats`}</span>
				<Segmented<AgentsPrefs['scope']>
					compact={true}
					value={prefs.scope}
					onChange={(scope) => save({scope})}
					aria-label={t`Which chats`}
					options={[
						{value: 'all', children: t`All chats`},
						{value: 'konvo', children: t`Only from Konvo`},
					]}
					data-flx="konvo-agents.notifications.scope"
				/>
			</div>
		</div>
	);
});

export const NotificationMatrix: React.FC = observer(() => {
	const {i18n, t} = useLingui();
	const sources = useNotificationSourceItems(20);
	const agents = AgentsAccess.enabled;
	useEffect(() => {
		if (agents) void Agents.loadPrefs();
	}, [agents]);
	const rows: ReadonlyArray<{
		source: NotificationChannelSource;
		label: string;
		hint: string;
		logo: React.ReactNode;
		on: boolean;
	}> = [
		...sources.map((item) => ({
			source: item.source,
			label: item.label,
			hint: item.hint,
			logo: item.logo,
			on: item.value,
		})),
		...(agents
			? [
					{
						source: 'agents' as const,
						label: t`Agents`,
						hint: t`When your coding agents finish, fail or need you`,
						logo: <IconTerminal2 size={20} stroke={1.75} />,
						on: true,
					},
				]
			: []),
	];
	return (
		<div className={styles.table} data-flx="konvo-notifications.matrix">
			{/* Each checkbox names its source and channel, so the headings are for the eye only. */}
			<div className={styles.head} aria-hidden="true">
				<span className={styles.sourceHead}>{i18n._(SOURCE_DESCRIPTOR)}</span>
				{NOTIFICATION_CHANNELS.map((channel) => (
					<span key={channel} className={styles.channelHead}>
						{i18n._(CHANNEL_DESCRIPTORS[channel])}
					</span>
				))}
			</div>
			{rows.map((row) => (
				<div
					key={row.source}
					className={clsx(styles.row, !row.on && styles.rowOff)}
					data-flx={`konvo-notifications.matrix.${row.source}`}
				>
					<span className={styles.who}>
						<span className={styles.logo} aria-hidden="true">
							{row.logo}
						</span>
						<span className={styles.text}>
							<span className={styles.name}>{row.label}</span>
							<span className={styles.hint}>{row.hint}</span>
						</span>
					</span>
					{NOTIFICATION_CHANNELS.map((channel) => (
						<Cell key={channel} source={row.source} label={row.label} channel={channel} />
					))}
					{row.source === 'agents' && <AgentsDetail />}
				</div>
			))}
		</div>
	);
});
